'use client';

/**
 * RadioReg Pro — Extended IndexedDB Layer
 * Primary local source of truth for offline-first operation.
 * Extends the existing offlineSync.ts patient/study stores with
 * additional stores for: today's patients cache, history cache,
 * catalog cache, auth/role persistence, and BCV rate.
 */

const DB_NAME = 'radioreg_offline';
const DB_VERSION = 3; // bumped from 1 to add new stores

export const STORE_PATIENTS = 'patients';
export const STORE_STUDIES = 'patient_studies';
export const STORE_QUEUE = 'sync_queue';
export const STORE_TODAY_CACHE = 'today_patients_cache';
export const STORE_HISTORY_CACHE = 'history_patients_cache';
export const STORE_CATALOG_CACHE = 'catalog_cache';
export const STORE_AUTH_CACHE = 'auth_cache';
export const STORE_BCV_CACHE = 'bcv_cache';
export const STORE_INSUMOS_CACHE = 'insumos_cache';

// ── Open / upgrade DB ────────────────────────────────────────────────────────
let _dbPromise: Promise<IDBDatabase> | null = null;

export function openDB(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      const oldVersion = e.oldVersion;

      // v1 stores (from offlineSync.ts)
      if (oldVersion < 1) {
        if (!db.objectStoreNames.contains(STORE_PATIENTS)) {
          const ps = db.createObjectStore(STORE_PATIENTS, { keyPath: 'id' });
          ps.createIndex('centerId', 'centerId', { unique: false });
          ps.createIndex('synced', 'synced', { unique: false });
          ps.createIndex('lastModifiedAt', 'lastModifiedAt', { unique: false });
        }
        if (!db.objectStoreNames.contains(STORE_STUDIES)) {
          const ss = db.createObjectStore(STORE_STUDIES, { keyPath: 'id' });
          ss.createIndex('patientId', 'patientId', { unique: false });
          ss.createIndex('synced', 'synced', { unique: false });
        }
        if (!db.objectStoreNames.contains(STORE_QUEUE)) {
          db.createObjectStore(STORE_QUEUE, { keyPath: 'id', autoIncrement: true });
        }
      }

      // v2 stores — cache layers
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains(STORE_TODAY_CACHE)) {
          const tc = db.createObjectStore(STORE_TODAY_CACHE, { keyPath: 'id' });
          tc.createIndex('created_at', 'created_at', { unique: false });
        }
        if (!db.objectStoreNames.contains(STORE_HISTORY_CACHE)) {
          const hc = db.createObjectStore(STORE_HISTORY_CACHE, { keyPath: 'id' });
          hc.createIndex('registeredAt', 'registeredAt', { unique: false });
        }
        if (!db.objectStoreNames.contains(STORE_CATALOG_CACHE)) {
          db.createObjectStore(STORE_CATALOG_CACHE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_AUTH_CACHE)) {
          db.createObjectStore(STORE_AUTH_CACHE, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(STORE_BCV_CACHE)) {
          db.createObjectStore(STORE_BCV_CACHE, { keyPath: 'key' });
        }
      }

      // v3 stores
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains(STORE_INSUMOS_CACHE)) {
          db.createObjectStore(STORE_INSUMOS_CACHE, { keyPath: 'key' });
        }
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      _dbPromise = null;
      reject(req.error);
    };
  });
  return _dbPromise;
}

// ── Generic helpers ──────────────────────────────────────────────────────────
export function idbGet<T>(db: IDBDatabase, store: string, key: IDBValidKey): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

export function idbPut(db: IDBDatabase, store: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).put(value);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function idbDelete(db: IDBDatabase, store: string, key: IDBValidKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function idbGetAll<T>(db: IDBDatabase, store: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

export function idbClear(db: IDBDatabase, store: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function idbPutBatch(db: IDBDatabase, store: string, items: unknown[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const os = tx.objectStore(store);
    for (const item of items) {
      os.put(item);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Today's patients cache ───────────────────────────────────────────────────
export interface CachedTodayPatient {
  id: string;
  ticket_number: number | null;
  created_at: string;
  first_name: string;
  last_name: string;
  age: number | null;
  birth_date: string | null;
  cedula: string;
  studies: string[];
  total_amount: number;
  payment_methods: string[];
  bcv_rate_ves: number;
  arrival_number: number | null;
  turno_tecnico_name: string | null;
  turno_medico_name: string | null;
  status: string;
  _cachedAt: string;
}

export async function saveTodayPatientsCache(patients: CachedTodayPatient[]): Promise<void> {
  try {
    const db = await openDB();
    await idbClear(db, STORE_TODAY_CACHE);
    if (patients.length > 0) {
      await idbPutBatch(db, STORE_TODAY_CACHE, patients);
    }
  } catch (e) {
    console.warn('[IDB] saveTodayPatientsCache error:', e);
  }
}

export async function getTodayPatientsCache(): Promise<CachedTodayPatient[]> {
  try {
    const db = await openDB();
    return await idbGetAll<CachedTodayPatient>(db, STORE_TODAY_CACHE);
  } catch {
    return [];
  }
}

// ── History patients cache ───────────────────────────────────────────────────
export interface CachedHistoryPatient {
  id: string;
  firstName: string;
  lastName: string;
  cedula: string;
  cedulaPrefix: string;
  age: number | null;
  phone: string | null;
  city: string | null;
  notes: string | null;
  totalAmount: number;
  registeredAt: string;
  studies: Array<{
    id: string;
    studyId: string;
    studyName: string;
    priceUsd: number;
    projections: number;
  }>;
  paymentMethods: string[];
  bcvRate: number;
  arrivalNumber: number | null;
  turnoTecnicoName: string | null;
  cdQuemado: boolean;
  correoEnviado: boolean;
  _cachedAt: string;
}

export async function saveHistoryPatientsCache(patients: CachedHistoryPatient[]): Promise<void> {
  try {
    const db = await openDB();
    await idbClear(db, STORE_HISTORY_CACHE);
    if (patients.length > 0) {
      await idbPutBatch(db, STORE_HISTORY_CACHE, patients);
    }
  } catch (e) {
    console.warn('[IDB] saveHistoryPatientsCache error:', e);
  }
}

export async function getHistoryPatientsCache(): Promise<CachedHistoryPatient[]> {
  try {
    const db = await openDB();
    const all = await idbGetAll<CachedHistoryPatient>(db, STORE_HISTORY_CACHE);
    // Sort by registeredAt descending (most recent first)
    return all.sort((a, b) => b.registeredAt.localeCompare(a.registeredAt));
  } catch {
    return [];
  }
}

// ── Auth / role cache ────────────────────────────────────────────────────────
export interface CachedAuthData {
  key: string;
  profile: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    isActive: boolean;
    centerId: string | null;
  } | null;
  catalogAccess: string[];
  savedAt: string;
}

export async function saveAuthCache(data: Omit<CachedAuthData, 'key' | 'savedAt'>): Promise<void> {
  try {
    const db = await openDB();
    await idbPut(db, STORE_AUTH_CACHE, {
      key: 'auth',
      ...data,
      savedAt: new Date().toISOString(),
    });
  } catch (e) {
    console.warn('[IDB] saveAuthCache error:', e);
  }
}

export async function getAuthCache(): Promise<CachedAuthData | null> {
  try {
    const db = await openDB();
    const result = await idbGet<CachedAuthData>(db, STORE_AUTH_CACHE, 'auth');
    return result ?? null;
  } catch {
    return null;
  }
}

export async function clearAuthCache(): Promise<void> {
  try {
    const db = await openDB();
    await idbDelete(db, STORE_AUTH_CACHE, 'auth');
  } catch {}
}

// ── BCV rate cache ───────────────────────────────────────────────────────────
export interface CachedBCVRate {
  key: string;
  rate: number;
  savedAt: string;
}

export async function saveBCVRateCache(rate: number): Promise<void> {
  try {
    const db = await openDB();
    await idbPut(db, STORE_BCV_CACHE, {
      key: 'bcv',
      rate,
      savedAt: new Date().toISOString(),
    });
  } catch {}
}

export async function getBCVRateCache(): Promise<number | null> {
  try {
    const db = await openDB();
    const result = await idbGet<CachedBCVRate>(db, STORE_BCV_CACHE, 'bcv');
    return result?.rate ?? null;
  } catch {
    return null;
  }
}

// ── Catalog cache ────────────────────────────────────────────────────────────
export interface CachedStudy {
  id: string;
  code: string;
  name: string;
  body_region: string;
  projections: number;
  price_usd: number;
  is_active: boolean;
  catalog_type: string;
  technician_pay_usd: number | null;
  doctor_pay_usd: number | null;
  _cachedAt: string;
}

export async function saveCatalogCache(studies: CachedStudy[]): Promise<void> {
  try {
    const db = await openDB();
    await idbClear(db, STORE_CATALOG_CACHE);
    if (studies.length > 0) {
      await idbPutBatch(db, STORE_CATALOG_CACHE, studies);
    }
  } catch (e) {
    console.warn('[IDB] saveCatalogCache error:', e);
  }
}

export async function getCatalogCache(): Promise<CachedStudy[]> {
  try {
    const db = await openDB();
    return await idbGetAll<CachedStudy>(db, STORE_CATALOG_CACHE);
  } catch {
    return [];
  }
}
